'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  EmojiEmotions,
  Group,
  Badge,
  Checklist,
  DataUsage,
  AltRoute,
  Domain,
  Assessment,
  Smartphone,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import BrandName from '../../components/BrandName';
import WorkflowStepper from '../../components/WorkflowStepper';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  { title: 'Configure Surveys', description: 'Build forms tailored to your facility' },
  { title: 'Collect Feedback', description: 'Administer via web or mobile point-of-care' },
  { title: 'Structure Data', description: 'Capture standard metrics alongside free-text' },
  { title: 'Analyze Trends', description: 'Review aggregated feedback dashboards' },
  { title: 'Drive Improvement', description: 'Connect findings directly to QAPI actions' }
];

const painPoints = [
  'Satisfaction data scattered across paper forms, email, and verbal reports',
  'No consistent way to capture resident, family, and staff perspectives in one system',
  'Hard to track trends or compare results across time periods or facilities',
  'Feedback collected but not linked to QAPI, grievances, or improvement plans',
  'Hours lost compiling satisfaction evidence for leadership, corporate, or surveyors',
  'One-size-fits-all surveys that do not match each facility’s policies or priorities',
];

const capabilities = [
  {
    icon: EmojiEmotions,
    title: 'Resident Satisfaction Surveys',
    description: 'Capture how residents experience daily care, services, and quality of life through guided, facility-configured satisfaction surveys—structured for consistency and easy review.',
  },
  {
    icon: Group,
    title: 'Family Feedback Surveys',
    description: 'Give families a professional channel to share feedback on care quality, communication, and service satisfaction—supporting person-centered care and family engagement goals.',
  },
  {
    icon: Badge,
    title: 'Staff Engagement Surveys',
    description: 'Assess staff satisfaction, engagement, and workplace experience to identify retention risks, training needs, and culture opportunities before they affect care delivery.',
  },
  {
    icon: Checklist,
    title: 'Configurable Survey Forms',
    description: 'Use the Form Wizard to build surveys that match your facility’s language, categories, and rating scales—without custom development.',
  },
  {
    icon: DataUsage,
    title: 'Structured Data Capture',
    description: 'Move beyond free-text comments. Capture ratings, categories, and structured responses that support reporting, trending, and QAPI review.',
  },
  {
    icon: AltRoute,
    title: 'Stakeholder-Specific Workflows',
    description: 'Separate survey paths for residents, families, and staff so each audience gets relevant questions and appropriate collection methods.',
  },
  {
    icon: Domain,
    title: 'Facility-Level Organization',
    description: 'Manage surveys per facility with consistent processes across your organization—ideal for single-site operators and multi-facility enterprises.',
  },
  {
    icon: Assessment,
    title: 'QAPI-Ready Evidence',
    description: 'Turn satisfaction feedback into documented quality data your QAPI committee can review, trend, and act on—alongside grievances, incidents, and performance metrics.',
  },
  {
    icon: Smartphone,
    title: 'Mobile-Friendly Collection',
    description: 'Staff can administer surveys at the point of care using the CareAscendium mobile app—same forms, same data, same accountability.',
  },
];

const roles = [
  {
    title: 'Administrators & DONs',
    benefit: 'Visibility into resident, family, and staff satisfaction across the facility',
  },
  {
    title: 'QAPI & Quality Leaders',
    benefit: 'Structured feedback data for committee review and performance improvement',
  },
  {
    title: 'Social Services & Activities',
    benefit: 'Guided tools to capture resident and family perspectives consistently',
  },
  {
    title: 'HR & Workforce Leaders',
    benefit: 'Staff engagement insights to support retention and culture initiatives',
  },
  {
    title: 'Corporate / Multi-Site Operators',
    benefit: 'Consistent survey processes and comparable data across facilities',
  },
];

const outcomes = [
  { value: '3', label: 'Stakeholder voices', detail: 'Resident, family, and staff feedback in one platform' },
  { value: '↑', label: 'Engagement evidence', detail: 'Documented satisfaction data for QAPI and surveys' },
  { value: 'Fast', label: 'Trend visibility', detail: 'Structured responses vs. manual spreadsheet compilation' },
  { value: '1', label: 'Source of truth', detail: 'Feedback linked to your broader quality ecosystem' },
];

const integrations = [
  'QAPI',
  'Grievance Management',
  'Committee & Meeting Management',
  'Action Plans',
  'Form Wizard',
  'Metrics & Dashboards',
  'Document Vault',
];

const faqs = [
  {
    q: 'Can we customize our own survey questions?',
    a: 'Yes. Surveys are built with the Form Wizard, so each facility can configure questions, rating scales, and categories to match policy and operational needs.',
  },
  {
    q: 'What types of feedback surveys are supported?',
    a: 'CareAscendium supports resident satisfaction surveys, family feedback forms, and staff engagement surveys—each with its own guided workflow.',
  },
  {
    q: 'How does this connect to QAPI?',
    a: 'Satisfaction and feedback data feed your quality improvement process alongside grievances, incidents, committee meetings, and action plans—giving leadership a complete picture of stakeholder experience.',
  },
  {
    q: 'Is this only for single facilities?',
    a: 'No. Multi-facility operators can deploy consistent survey processes across every location in their enterprise.',
  },
  {
    q: 'Can staff administer surveys on mobile?',
    a: 'Yes. Surveys can be completed through the CareAscendium web and mobile experience for flexible collection at the point of care.',
  },
];

export default function FeedbackAndSatisfactionSurveysClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Feedback & Satisfaction Surveys | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Feedback & Satisfaction Surveys"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            Hear From Residents, Families, and Staff—Then Turn Feedback Into Measurable Quality Improvement
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> gives skilled nursing facilities configurable satisfaction and engagement surveys for every key stakeholder—family members, residents, and staff—with structured data capture, facility-specific forms, and a direct line from feedback to QAPI and continuous improvement.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              Most facilities collect satisfaction feedback on paper, in ad hoc conversations, or through disconnected tools. Results sit in binders or spreadsheets. Leadership struggles to compare trends over time. Quality teams cannot easily connect survey findings to grievances, action plans, or committee discussions. When surveyors ask for evidence of resident and family engagement, the proof is hard to assemble quickly.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;Feedback isn't just about hearing from people—it's about turning what you hear into structured, measurable data that your QAPI committee can actually act on.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              From Voice to Action
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              <BrandName variant="light" style={{ fontSize: 'inherit' }} /> moves your organization past disjointed paper surveys. We provide structured workflows that connect resident, family, and staff voices directly into your quality management ecosystem.
            </Typography>
          </Box>
          <WorkflowStepper steps={workflowSteps} />
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Who Benefits
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  width: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Platform
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                  Feedback isn't isolated. Our surveys tie directly into your broader operational framework:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Continuous engagement and improvement.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            FAQs
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Transform Feedback Into Action
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> standardizes surveys for your facility, delivering the quantitative data you need to drive QAPI and engage your community.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
